export type { TicketStatus } from '@sd/constants/ticketStatuses';
export { DEFAULT_TICKET_STATUS } from '@sd/constants/ticketStatuses';
import type { TicketStatus } from '@sd/constants/ticketStatuses';
export type TicketPriority = 'P1' | 'P2' | 'P3' | 'P4';
export type OverdueLevel = 'none' | 'warning' | 'critical';
export type TicketType = 'incident' | 'request' | 'access' | 'issue' | 'new_requirement' | 'suggestion';
export type SLAState = 'active' | 'paused' | 'breached' | 'completed';
export type ContactMethod = 'email' | 'phone' | 'chat' | 'portal';

export interface User {
  id: string;
  name: string;
  email: string;
  company: string;
  department: string;
  phone: string;
  timezone: string;
  supportTier: string;
  avatar?: string;
  preferredContactMethod?: ContactMethod;
  bestTimeToContact?: string;
}

export interface Asset {
  id: string;
  name: string;
  type: 'slack' | 'microsoft365' | 'desktop';
  status: 'online' | 'offline';
}

export interface Attachment {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
  /** Absolute https:// media URL (file_path from API) — used as direct video/image fallback. */
  mediaUrl?: string;
  /** Smaller preview from file-utility when available (cards / stream). */
  thumbnailUrl?: string;
  uploadedBy: string;
  uploadedAt: string;
  visibility: 'public' | 'internal';
}

export interface SLAPauseReason {
  reason: 'waiting_on_customer' | 'internal_dependency' | 'reassignment' | 'other';
  description?: string;
  pausedAt: string;
  resumedAt?: string;
}

export interface SLATimer {
  type: 'first_response' | 'next_response' | 'resolution';
  label: string;
  time: string;
  isBreached: boolean;
  state: SLAState;
  pauseHistory?: SLAPauseReason[];
  breachReason?: string;
  atRisk?: boolean;
}

export interface TicketNote {
  id: string;
  type: 'private' | 'public';
  content: string;
  author: string;
  timestamp: string;
  attachments?: Attachment[];
  mentions?: string[];
  editHistory?: { content: string; editedAt: string; editedBy: string }[];
}

export interface AuditLogEntry {
  id: string;
  action: 'created' | 'status_change' | 'priority_change' | 'assigned' | 'escalated' | 'viewed' | 'attachment_added' | 'note_added' | 'call_initiated' | 'email_sent' | 'reopened' | 'resolved' | 'closed' | 'sla_paused' | 'sla_resumed' | 'auto_close_extended' | 'auto_closed';
  actor: string;
  timestamp: string;
  details?: string;
  previousValue?: string;
  newValue?: string;
}

export interface ResolutionDetails {
  code: string;
  notes: string;
  resolvedBy: string;
  resolvedAt: string;
  slaBreach?: boolean;
}

export interface ReopenDetails {
  reason: string;
  reopenReason: string;
  reopenedBy: string;
  reopenedAt: string;
  slaRestarted: boolean;
  priorityEscalated?: boolean;
  previousResolution?: ResolutionDetails;
}

export interface Ticket {
  id: string;
  /** Human-readable id from API (e.g. TXT-10017). */
  uiId?: string;
  title: string;
  description?: string;
  status: TicketStatus;
  priority: TicketPriority;
  /** UUID from GET /service_desk/priority_listing/ (`priority_id` on ticket). */
  priorityId?: string;
  /** Human-readable label resolved from `priority_id` (e.g. "P2 - High"). */
  priorityLabel?: string;
  ticketType: TicketType;
  /** Raw API `tickettype` when it differs from normalized `ticketType`. */
  ticketTypeRaw?: string;
  isVip?: boolean;
  isSla?: boolean;
  /** API `is_exit` flag — when explicitly false the agent cannot edit the ticket. */
  isExit?: boolean;
  /** API `is_pts` flag controls which assignee source the agent panel uses. */
  isPts?: boolean;
  /** API project UUID, used for project-scoped assignee options. */
  projectId?: string;
  /** Human-readable project name from API `project_name`. */
  projectName?: string;
  /** Nested `change_request` from ticket_create (Admin / Super Agent). */
  changeRequest?: {
    id?: string;
    crNumber?: string;
    requestedDate?: string;
    expectedCompletion?: string;
    assignedEmployeeIds?: string[];
    status?: string;
  };
  user: User;
  assignee?: string;
  owner?: string;
  overdue?: string | null;
  overdueLevel?: OverdueLevel;
  notes: TicketNote[];
  slaTimers: SLATimer[];
  attachments?: Attachment[];
  /** Upload-service file UUIDs from API `attachments` (e.g. `"[\"uuid\"]"`). */
  attachmentFileIds?: string[];
  auditLog?: AuditLogEntry[];
  resolution?: ResolutionDetails;
  reopenHistory?: ReopenDetails[];
  category?: string;
  subcategory?: string;
  tags?: string[];
  parentTicketId?: string;
  relatedTicketIds?: string[];
  channel?: string;
  sourceMetadata?: {
    browser?: string;
    os?: string;
    appVersion?: string;
  };
  createdAt: string;
  updatedAt: string;
  closedAt?: string;
  /** When API sends resolved timestamp without full `resolution` payload (agent panel). */
  resolvedAt?: string;
  autoCloseAt?: string;
  /** Deadline timestamp from API (ISO format). */
  deadline?: string;
}

export interface QueueItem {
  id: string;
  label: string;
  icon: string;
  count: number;
}

export interface SavedFilter {
  id: string;
  name: string;
  filters: {
    status?: TicketStatus[];
    priority?: TicketPriority[];
    ticketType?: TicketType[];
    assignee?: string;
    tags?: string[];
    dateRange?: { start: string; end: string };
  };
  createdBy: string;
  isShared: boolean;
}

export const RESOLUTION_CODES = [
  { code: 'FIXED', label: 'Fixed' },
  { code: 'WORKAROUND', label: 'Workaround Provided' },
  { code: 'USER_EDUCATION', label: 'User Education' },
  { code: 'CONFIGURATION_CHANGE', label: 'Configuration Change' },
  { code: 'HARDWARE_REPLACEMENT', label: 'Hardware Replacement' },
  { code: 'SOFTWARE_UPDATE', label: 'Software Update' },
  { code: 'ACCESS_GRANTED', label: 'Access Granted' },
  { code: 'DUPLICATE', label: 'Duplicate - Merged' },
  { code: 'CANNOT_REPRODUCE', label: 'Cannot Reproduce' },
  { code: 'NOT_A_BUG', label: 'Not a Bug / By Design' },
  { code: 'THIRD_PARTY', label: 'Third Party Issue' },
  { code: 'OTHER', label: 'Other' },
] as const;

export const TICKET_TYPES = [
  { value: 'issue', label: 'Issue', description: 'General issue' },
  { value: 'new_requirement', label: 'New Requirement', description: 'New feature or requirement' },
  { value: 'suggestion', label: 'Suggestion', description: 'Improvement or suggestion' },
  { value: 'incident', label: 'Incident', description: 'Something is broken' },
  { value: 'request', label: 'Request', description: 'A service request' },
  { value: 'access', label: 'Access', description: 'Access or permissions' },
] as const;

/** Options for admin/agent ticket list Type filter (API query param `type`). */
export const TICKET_TYPE_FILTER_OPTIONS = [
  { value: "issue", label: "Issue" },
  { value: "new_requirement", label: "New Requirement" },
  { value: "suggestion", label: "Suggestion" },
] as const;

/** Options for create/edit ticket form (searchable dropdown). */
export const TICKET_TYPE_CREATE_OPTIONS = [
  { value: "issue", label: "Issue" },
  { value: "new_requirement", label: "New Requirement" },
  { value: "suggestion", label: "Suggestion" },
] as const;

/** Map form selection to API `tickettype` param. */
export function ticketTypeForApi(formType: string, fallback = "issue"): string {
  const norm = normalizeTicketTypeFromApi(formType);
  if (
    norm === "issue" ||
    norm === "new_requirement" ||
    norm === "suggestion"
  ) {
    return norm;
  }
  return normalizeTicketTypeFromApi(fallback);
}

/** Map API `tickettype` (`issue` | `new_requirement` | legacy values) to `TicketType`. */
export function normalizeTicketTypeFromApi(raw?: string): TicketType {
  if (!raw?.trim()) return 'issue';
  const s = raw.trim().toLowerCase().replace(/\s+/g, '_');
  if (
    s === 'new_requirement' ||
    (s.includes('new') && s.includes('requirement'))
  ) {
    return 'new_requirement';
  }
  if (s === 'incident' || s === 'request' || s === 'access' || s === 'issue' || s === 'suggestion') {
    return s;
  }
  if (s.includes('service') && s.includes('request')) return 'request';
  if (s === 'problem') return 'issue';
  return 'issue';
}

function formatRawTicketTypeLabel(raw?: string): string {
  const s = (raw ?? '').trim();
  if (!s) return '—';
  return s
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/** Human label for ticket type — matches edit form (`Issue` / `New Requirement`). */
export function formatTicketTypeLabel(
  type?: string,
  rawFallback?: string
): string {
  const norm = normalizeTicketTypeFromApi(type ?? rawFallback);
  const found = TICKET_TYPES.find(t => t.value === norm);
  if (found) return found.label;
  return formatRawTicketTypeLabel(type ?? rawFallback);
}

/** Compact label for STREAM list — avoids "New" next to status "New". */
export function formatTicketTypeLabelCompact(
  type?: string,
  rawFallback?: string
): string {
  const norm = normalizeTicketTypeFromApi(type ?? rawFallback);
  if (norm === "new_requirement") return "Requirement";
  return formatTicketTypeLabel(type, rawFallback);
}

export const SLA_PAUSE_REASONS = [
  { value: 'waiting_on_customer', label: 'Waiting on Customer' },
  { value: 'internal_dependency', label: 'Internal Dependency' },
  { value: 'reassignment', label: 'Reassignment' },
  { value: 'other', label: 'Other' },
] as const;
